import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { fetchOrderInvoice, productPath, type OrderInvoiceResponse } from "../../lib/catalog";
import { formatCurrency, toSentenceCase } from "../../lib/utils";

function invoiceState(order: Pick<OrderInvoiceResponse["order"], "total" | "amountPaid">) {
  if ((order.total || 0) > 0 && (order.amountPaid || 0) >= (order.total || 0)) {
    return "Paid";
  }
  if ((order.amountPaid || 0) > 0) {
    return "Part paid";
  }
  return "Unpaid";
}

function invoiceStateTone(state: string) {
  if (state === "Paid") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (state === "Part paid") {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }
  return "border-red-200 bg-red-50 text-red-700";
}

export function SalesInvoicePage() {
  const [params] = useSearchParams();
  const orderId = params.get("order");
  const [data, setData] = useState<OrderInvoiceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;

    async function loadInvoice() {
      if (!orderId) {
        setError("Missing order reference.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const query = new URLSearchParams();
        query.set("id", orderId);
        const result = await fetchOrderInvoice(query);
        if (!ignore) {
          setData(result);
        }
      } catch {
        if (!ignore) {
          setError("Unable to load this invoice.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void loadInvoice();
    return () => {
      ignore = true;
    };
  }, [orderId]);

  const qrValue =
    data && orderId
      ? encodeURIComponent(
          `Novatech Invoice #${data.order.id}\nReference: ${data.order.trackingNumber || "N/A"}\nOrder: ${orderId}\nStatus: ${invoiceState(data.order)}`
        )
      : null;
  const qrImageUrl = qrValue ? `https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${qrValue}` : null;
  const state = data ? invoiceState(data.order) : "Unpaid";

  return (
    <section className="invoice-page mx-auto w-full max-w-5xl px-3 py-12 sm:px-4 lg:px-5">
      {loading ? (
        <div className="glass-panel rounded-[32px] p-8 text-sm text-nv-slate">Loading invoice...</div>
      ) : error || !data ? (
        <div className="glass-panel rounded-[32px] p-8">
          <h1 className="text-3xl font-semibold tracking-tight">Invoice unavailable</h1>
          <p className="mt-3 text-sm text-nv-slate">{error || "This invoice could not be found."}</p>
          <Link className="brand-button mt-6 inline-flex rounded-full px-5 py-3 text-sm font-semibold text-white" to="/sales/invoices">
            Back to invoices
          </Link>
        </div>
      ) : (
        <div className="invoice-shell space-y-6">
          <div className="invoice-actions flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="eyebrow">Invoice</p>
              <h1 className="mt-2 text-4xl font-bold tracking-tight">Order #{data.order.id}</h1>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link className="rounded-full border border-black/10 px-5 py-3 text-sm font-semibold" to="/sales/invoices">
                Back
              </Link>
              <button className="rounded-full border border-black/10 px-5 py-3 text-sm font-semibold" onClick={() => window.print()} type="button">
                Download / Print
              </button>
              <span className={`rounded-full border px-4 py-3 text-sm font-semibold ${invoiceStateTone(state)}`}>
                {state}
              </span>
            </div>
          </div>

          <div className="invoice-actions rounded-[28px] border border-black/10 bg-black/5 p-5 text-sm leading-7 text-nv-slate">
            Use this invoice for customer sharing, payment follow-up, or print/export from the sales desk.
          </div>

          <div className="glass-panel rounded-[32px] p-8 print:rounded-none">
            <div className="flex flex-wrap items-start justify-between gap-8 border-b border-black/10 pb-8">
              <div>
                <img alt="Novatech Logo" className="h-14 w-14 object-contain" src="/logo/logo.jpg" />
                <h2 className="mt-4 text-2xl font-semibold">Novatech</h2>
                <p className="mt-2 text-sm leading-7 text-nv-slate">
                  Royal Palms Mall, Mfangano Street
                  <br />
                  +254795952128
                  <br />
                  novatech.co.ke
                </p>
              </div>
              <div className="grid gap-3 text-sm text-nv-slate sm:text-right">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em]">Status</p>
                  <p className={`mt-1 inline-flex rounded-full border px-3 py-1 text-sm font-semibold ${invoiceStateTone(state)}`}>
                    {state}
                  </p>
                </div>
                {data.order.addedOn ? (
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em]">Issued</p>
                    <p className="mt-1 text-base text-nv-ink">{new Date(data.order.addedOn).toLocaleString()}</p>
                  </div>
                ) : null}
                {data.order.trackingNumber ? (
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em]">Reference</p>
                    <p className="mt-1 text-base text-nv-ink">{data.order.trackingNumber}</p>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="grid gap-6 border-b border-black/10 py-8 sm:grid-cols-2">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-nv-slate">Bill to</p>
                <div className="mt-3 space-y-2 text-sm text-nv-slate">
                  <p className="font-semibold text-nv-ink">{data.order.deliveryName}</p>
                  {data.order.email ? <p>{data.order.email}</p> : null}
                  <p>{data.order.deliveryPhone}</p>
                  <p className="whitespace-pre-line">{data.order.deliveryAddress}</p>
                  {data.order.deliveryNote ? <p>Note: {data.order.deliveryNote}</p> : null}
                </div>
              </div>
            </div>

            <div className="overflow-x-auto py-8">
              <table className="w-full table-fixed">
                <thead>
                  <tr className="border-b border-black/10 text-left text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">
                    <th className="w-[52%] py-3 pr-4">Item</th>
                    <th className="w-[12%] py-3">Qty</th>
                    <th className="w-[16%] py-3">Unit price</th>
                    <th className="w-[20%] py-3 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item) => (
                    <tr className="border-b border-black/5 align-top" key={item.id}>
                      <td className="py-4 pr-6">
                        {item.productId && item.slug ? (
                          <Link className="text-sm font-semibold hover:text-nv-electric" to={productPath(item.slug, item.productId)}>
                            {toSentenceCase(item.name)}
                          </Link>
                        ) : (
                          <span className="text-sm font-semibold">{toSentenceCase(item.name)}</span>
                        )}
                      </td>
                      <td className="py-4 text-sm">{item.quantity}</td>
                      <td className="py-4 text-sm">{formatCurrency(item.unitPrice)}</td>
                      <td className="py-4 text-right text-sm font-semibold">{formatCurrency(item.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="ml-auto grid max-w-sm gap-3 border-t border-black/10 pt-6 text-sm">
              <div className="flex items-center justify-between text-nv-slate">
                <span>Subtotal</span>
                <span>{formatCurrency(data.order.subtotal)}</span>
              </div>
              <div className="flex items-center justify-between text-nv-slate">
                <span>Discount</span>
                <span>{formatCurrency(data.order.discount)}</span>
              </div>
              <div className="flex items-center justify-between text-nv-slate">
                <span>Paid</span>
                <span>{formatCurrency(data.order.amountPaid)}</span>
              </div>
              <div className="flex items-center justify-between border-t border-black/10 pt-3 text-base font-semibold">
                <span>Balance due</span>
                <span>{formatCurrency(data.order.balance)}</span>
              </div>
            </div>

            {qrImageUrl ? (
              <div className="mt-8 flex justify-center border-t border-black/10 pt-6">
                <div className="rounded-[28px] border border-black/10 bg-black/5 p-4 text-center">
                  <img alt={`QR code for invoice ${data.order.id}`} className="mx-auto h-32 w-32 object-contain" src={qrImageUrl} />
                  <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-nv-slate">Scan invoice</p>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </section>
  );
}
